import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import CardHeader from '@zenbusiness/zui/CardHeader';
import ErrorState from '@zenbusiness/zui/ErrorState';
import Link from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { useProductCategories } from '#client/hooks/useProductCategories';
import { PRODUCTS_FILTER_PARAM_KEY, PRODUCTS_KEY } from '#client/pages/Products/constants';
import { formatSearchParams } from '#client/utils/formatString';

import { HomeSection } from '../HomeSection';
import * as Styled from './ProductCategories.styles';
import { ProductBanners } from '../ProductBanners';
import { LoadingState } from '../Widgets/components/LoadingState';
import { WidgetLink } from '../Widgets/components/WidgetLink';

function ProductCategories() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const productsPath = useDashboardPath(`/${PRODUCTS_KEY}`);
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: 'dashboard.productCategories.title' });
  const {
    data: categories, error, loading, refetch
  } = useProductCategories();

  const imageUrl = isMobile
    ? '/s2/raster/wfc-banner-assets-mobile.png'
    : '/s2/raster/wfc-banner-assets-dt.png';

  const eventProps = {
    appSection: 'home',
    elementValue: `${formatSearchParams(title)}_widget`
  };

  const clickstreamProps = {
    elementName: 'product_category_link',
    elementPosition: 'product_categories',
    ...eventProps
  };

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState onReload={refetch} />;
  }

  if (!categories.length) {
    return null;
  }

  return (
    <HomeSection
      title={(<Text color="primary" size="xl" weight="medium">{title}</Text>)}
      slots={{
        aside: <WidgetLink
          url={productsPath}
          text={formatMessage({ id: 'dashboard.productCategories.button.label' })}
          eventProps={eventProps}
          icon="/s2/material-icons/arrowforwardrounded.svg"
        />
      }}
      slotProps={{
        primary: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          sx: {
            '& .ZuiPageHeader-aside': {
              minWidth: 'fit-content'
            }
          }
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        <ProductBanners
          imageUrl={imageUrl}
          title="dashboard.productCategories.banner1.title"
          description="dashboard.productCategories.banner1.description"
        />

        <Styled.CardList>
          {categories.map((item) => {
            const itemTitle = formatMessage({ id: item.titleKey });

            return (
              <Styled.CardItem
                component={Link}
                key={item.uuid}
                clickstreamProps={clickstreamProps}
                aria-label={itemTitle}
                title={itemTitle}
                to={`${PRODUCTS_KEY}?${PRODUCTS_FILTER_PARAM_KEY}=${formatSearchParams(itemTitle)}`}
              >
                {item.imagePath && (
                <SharedAsset alt="" color="text.blue" src={item.imagePath} width={24} />
                )}
                <CardHeader
                  subtitle={formatMessage({ id: item.descriptionKey })}
                  title={(<Text color="primary" size="sm" weight="medium"><FormattedMessage id={item.titleKey} /></Text>)}
                />
              </Styled.CardItem>
            );
          })}
        </Styled.CardList>
      </Box>
    </HomeSection>
  );
}

export default ProductCategories;
